// Excel / xlsx input parsing for the new-supply calculation.
//
// Mirrors the CSV template (examples/csv-template.md):
//   Sheet "PARCEL": two columns "key", "value"
//   Sheet "PRICES": two columns "key", "value"
//   Sheet "ITEMS":  one header row, then data rows
//
// Per the engineering rule "xlsx support as input/output layer only",
// this module maps the workbook into the same JSON shape produced by
// the CSV parser and then defers to `validateDemoFile` for structural
// validation. No land-price rule logic lives here.

import ExcelJS, { type CellValue } from "exceljs";

import { validateDemoFile } from "./validate.js";
import type { DemoNewSupplyFile } from "./input.js";
import type { ValidationMessage } from "../types/validation.js";

export interface ParseNewSupplyXlsxResult {
  readonly value: DemoNewSupplyFile | null;
  readonly errors: ValidationMessage[];
}

export async function parseNewSupplyXlsx(
  buffer: Uint8Array | Buffer,
): Promise<ParseNewSupplyXlsxResult> {
  const errors: ValidationMessage[] = [];

  const workbook = new ExcelJS.Workbook();
  try {
    // exceljs's load() signature accepts an ArrayBuffer; convert
    // through a copy if we get a Node Buffer or a view-backed
    // Uint8Array.
    const ab = toArrayBuffer(buffer);
    await workbook.xlsx.load(ab);
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    return {
      value: null,
      errors: [
        {
          level: "error",
          field: "<root>",
          message: `无法解析 xlsx：${reason}`,
        },
      ],
    };
  }

  const parcelRows = readKvSheet(workbook, "PARCEL", errors);
  const pricesRows = readKvSheet(workbook, "PRICES", errors);
  const itemsRows = readItemsSheet(workbook, "ITEMS", errors);

  if (errors.length > 0) {
    return { value: null, errors };
  }
  if (!parcelRows || !pricesRows || !itemsRows) {
    // Should not happen because readKvSheet/readItemsSheet push
    // errors on failure. Defensive.
    return { value: null, errors: [...errors, { level: "error", field: "<root>", message: "xlsx 解析失败。" }] };
  }

  const parcel = kvRowsToObject(parcelRows, "[PARCEL]", errors, PARCEL_STRING_KEYS);
  const prices = kvRowsToObject(pricesRows, "[PRICES]", errors, PRICE_STRING_KEYS);
  const items = itemsRowsToArray(itemsRows, "[ITEMS]", errors);

  if (errors.length > 0 || !parcel || !prices || !items) {
    return { value: null, errors };
  }

  const jsonInput = {
    calculationType: "new-supply" as const,
    input: { parcel, prices, items },
  };
  const validated = validateDemoFile(jsonInput);
  if (validated.errors.length > 0 || validated.value === null) {
    return { value: null, errors: validated.errors };
  }
  return { value: validated.value as DemoNewSupplyFile, errors: [] };
}

// ---------------------------------------------------------------------------
// Sheet readers
// ---------------------------------------------------------------------------

type Cell = string | number | boolean | Date | null | undefined;
type Row = Cell[];

function toArrayBuffer(input: Uint8Array | Buffer): ArrayBuffer {
  // exceljs wants a real ArrayBuffer; copy out of any view / Buffer
  // to make sure the underlying buffer isn't shared.
  const view = input instanceof Uint8Array ? input : new Uint8Array(input);
  const ab = new ArrayBuffer(view.byteLength);
  new Uint8Array(ab).set(view);
  return ab;
}

function readKvSheet(
  workbook: ExcelJS.Workbook,
  name: string,
  errors: ValidationMessage[],
): Row[] | null {
  const sheet = workbook.getWorksheet(name);
  if (!sheet) {
    errors.push({
      level: "error",
      field: `[${name}]`,
      message: `缺少 [${name}] 工作表。`,
    });
    return null;
  }
  const rows: Row[] = [];
  sheet.eachRow({ includeEmpty: false }, (row) => {
    const a = stringifyCell(row.getCell(1).value);
    const b = stringifyCell(row.getCell(2).value);
    if (a === "" && b === "") return;
    rows.push([a, b ?? ""]);
  });
  if (rows.length === 0) {
    errors.push({
      level: "error",
      field: `[${name}]`,
      message: `[${name}] 工作表为空（至少需要一行 key,value）。`,
    });
    return null;
  }
  // First row is treated as a header in CSV; for xlsx, the first
  // row is also data (key,value). Drop it if the cell looks like
  // a header label.
  const firstKey = (rows[0][0] ?? "").toString().trim().toLowerCase();
  if (firstKey === "key" || firstKey === "字段" || firstKey === "name") {
    rows.shift();
  }
  return rows;
}

function readItemsSheet(
  workbook: ExcelJS.Workbook,
  name: string,
  errors: ValidationMessage[],
): Row[] | null {
  const sheet = workbook.getWorksheet(name);
  if (!sheet) {
    errors.push({
      level: "error",
      field: `[${name}]`,
      message: `缺少 [${name}] 工作表。`,
    });
    return null;
  }
  const rows: Row[] = [];
  sheet.eachRow({ includeEmpty: false }, (row) => {
    const cells: Cell[] = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cells[colNumber - 1] = stringifyCell(cell.value);
    });
    // Skip rows where every cell is empty.
    if (cells.every((c) => c === "" || c === undefined || c === null)) return;
    rows.push(cells);
  });
  if (rows.length === 0) {
    errors.push({
      level: "error",
      field: `[${name}]`,
      message: `[${name}] 工作表为空（至少需要字段名表头）。`,
    });
    return null;
  }
  return rows;
}

function stringifyCell(v: CellValue): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "object") {
    // exceljs may wrap rich text; use the .text property if present.
    const obj = v as { text?: unknown; result?: unknown };
    if (typeof obj.text === "string") return obj.text;
    if (typeof obj.result === "string") return obj.result;
  }
  return String(v);
}

// ---------------------------------------------------------------------------
// Section → domain object (mirrors src/demo/csv-input.ts)
// ---------------------------------------------------------------------------

function kvRowsToObject(
  rows: Row[],
  sectionPath: string,
  errors: ValidationMessage[],
  stringKeys: ReadonlySet<string> = new Set(),
): Record<string, unknown> | null {
  const obj: Record<string, unknown> = {};
  let ok = true;
  rows.forEach((row, idx) => {
    const path = `${sectionPath}[${idx}]`;
    if (row.length < 2 || (row[0] ?? "").toString().trim() === "") {
      errors.push({
        level: "error",
        field: path,
        message: `${path} 必须是 key,value 形式（两列）。`,
      });
      ok = false;
      return;
    }
    const key = (row[0] ?? "").toString().trim();
    const raw = (row[1] ?? "").toString();
    if (obj[key] !== undefined) {
      errors.push({
        level: "error",
        field: `${path}.${key}`,
        message: `${path}.${key} 重复定义。`,
      });
      ok = false;
      return;
    }
    obj[key] = coerceScalar(raw, `${path}.${key}`, errors, stringKeys.has(key));
  });
  return ok ? obj : null;
}

function itemsRowsToArray(
  rows: Row[],
  sectionPath: string,
  errors: ValidationMessage[],
): Record<string, unknown>[] | null {
  if (rows.length === 0) {
    errors.push({
      level: "error",
      field: sectionPath,
      message: `${sectionPath} 段不能为空（至少需要字段名表头）。`,
    });
    return null;
  }
  const header = rows[0].map((c) => (c ?? "").toString().trim());
  if (header.length === 0 || header[0] === "") {
    errors.push({
      level: "error",
      field: `${sectionPath}[0]`,
      message: `${sectionPath} 表头必须至少包含一个列名。`,
    });
    return null;
  }
  if (rows.length === 1) return [];
  const out: Record<string, unknown>[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const obj: Record<string, unknown> = {};
    for (let c = 0; c < header.length; c++) {
      const key = header[c];
      const raw = (row[c] ?? "").toString();
      obj[key] = coerceScalar(raw, `${sectionPath}[${r}].${key}`, errors);
    }
    out.push(obj);
  }
  return out;
}

function coerceScalar(
  raw: string,
  field: string,
  errors: ValidationMessage[],
  forceString: boolean = false,
): unknown {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (forceString) return trimmed;
  if (/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
    errors.push({
      level: "error",
      field,
      message: `${field} 不是合法数字：${raw}`,
    });
    return raw;
  }
  return trimmed;
}

const PARCEL_STRING_KEYS: ReadonlySet<string> = new Set([
  "projectName",
  "parcelId",
  "supplyMethod",
  "calculationType",
  "calculationDate",
  "mainLandUse",
  "projectType",
  "remark",
]);

const PRICE_STRING_KEYS: ReadonlySet<string> = new Set([
  "priceSource",
  "priceDate",
  "marketPriceSource",
  "marketPriceYear",
  "valuationDate",
  "effectiveDate",
  "standardParcelCode",
  "benchmarkZoneCode",
  "officialQueryUrl",
  "priceSourceRemark",
]);